<template>
    <div style="display: flex; flex-direction: row; flex-wrap: wrap;" class="q-mt-xs q-mb-sm">
        <div v-if="items.length == 0" class="text-caption q-ml-md text-grey-7">
            {{ Translate('No items visible - check your filter settings') }}
        </div>
        <div v-else v-for="(metric,j) in items" :key="j" style="width: 320px; max-width: 320px; display: flex; flex-direction: row;" class="q-mr-md">
            <q-btn
                v-if="(metric.MetricAggs.Type == 'function' || metric.MetricAggs.Type == 'filter' || metric.MetricAggs.Type == 'script' || metric.MetricAggs.Type == 'filter_script') && metric.MetricAggs.FunctionList.length > 1"
                flat
                dense
                no-caps
                class="text-weight-regular subtitle-1 q-py-none"
                style="color: var(--v-primaryText-base);"
                :disable="isDisabledSource(metric)"
                @click="metricMenu = []"
            >
                <q-icon class="q-mr-xs" :color="getMetricIconColor(metric)" :name="getMetricItemIcon(metric)" />
                <span class="ellipsis" style="max-width:210px;">{{ metric.Title }}</span>
                <q-icon name="mdi-menu-down" style="color: var(--v-secondaryText-base);" />
                <q-menu
                    :disable="isDisabledSource(metric)"
                    anchor="bottom left"
                    self="top left"
                    :close-on-content-click="false"
                >
                    <q-list dense>
                        <q-item
                            v-for="func in getSortedFunctions(metric)"
                            :key="func"
                            clickable
                            v-ripple
                            @click="isDisabledMetric(metric, func) ? removeMetric(metric, func) : addMetric(metric, func)"
                        >
                            <q-item-section avatar>
                                <q-icon :color="isDisabledMetric(metric, func) ? 'primary' : undefined" :name="isDisabledMetric(metric, func) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'" />
                            </q-item-section>
                            <q-item-section>{{ getFunctionDisplayName(metric, func) }}</q-item-section>
                        </q-item>
                    </q-list>
                </q-menu>
            </q-btn>
            <q-btn
                v-if="(metric.MetricAggs.Type == 'function' || metric.MetricAggs.Type == 'filter' || metric.MetricAggs.Type == 'script' || metric.MetricAggs.Type == 'filter_script') && metric.MetricAggs.FunctionList.length == 1"
                flat
                dense
                no-caps
                class="text-weight-regular subtitle-1 q-py-none"
                style="color: var(--v-primaryText-base);"
                :disable="isDisabledSource(metric)"
                @click="(evt) => isDisabledMetric(metric, metric.MetricAggs.FunctionList[0]) ? removeMetric(metric, metric.MetricAggs.FunctionList[0]) : addMetric(metric, metric.MetricAggs.FunctionList[0])"
            >
                <q-icon v-if="isMetricInUse(metric)" class="q-mr-xs" color="primary" name="mdi-checkbox-marked" />
                <q-icon v-else class="q-mr-xs" name="mdi-checkbox-blank-outline" />
                <span class="ellipsis" :style="{ maxWidth: '230px' }">{{ getMetricDisplayName(metric, metric.MetricAggs.FunctionList[0]) }}</span>
            </q-btn>
            <q-btn
                v-if="metric.MetricAggs.Type == 'bucket_script'"
                flat
                dense
                no-caps
                class="text-weight-regular subtitle-1 q-py-none"
                style="color: var(--v-primaryText-base);"
                :disable="isDisabledSource(metric)"
                @click="(evt) => isDisabledMetric(metric, 'bucket') ? removeMetric(metric, 'bucket') : addMetric(metric, 'bucket')"
            >
                <q-icon v-if="isMetricInUse(metric)" class="q-mr-xs" color="primary" name="mdi-checkbox-marked" />
                <q-icon v-else class="q-mr-xs" name="mdi-checkbox-blank-outline" />
                <span class="ellipsis" :style="{ maxWidth: '230px' }">{{ metric.Title }}</span>
            </q-btn>
            <q-btn
                v-if="metric.MetricAggs.Type == 'placeholder'"
                flat
                dense
                no-caps
                class="text-weight-regular subtitle-1 q-py-none"
                style="color: var(--v-primaryText-base);"
                :disable="isDisabledSource(metric)"
                @click="(evt) => isDisabledMetric(metric, 'placeholder') ? removeMetric(metric, 'placeholder') : addMetric(metric, 'placeholder')"
            >
                <q-icon v-if="isMetricInUse(metric)" class="q-mr-xs" color="primary" name="mdi-checkbox-marked" />
                <q-icon v-else class="q-mr-xs" name="mdi-checkbox-blank-outline" />
                <span class="ellipsis" :style="{ maxWidth: '230px' }">{{ metric.Title }}</span>
            </q-btn>

            <q-space />

            <q-btn
                dense
                round
                flat
                size="sm"
                icon="mdi-information"
                class="q-ma-none q-py-none"
                style="color:var(--v-checkboxGrey-base)"
                @click="showHelp(metric)"
            >
                <q-tooltip anchor="bottom middle" self="top middle" :delay="350" transition-show="scale" transition-hide="scale">
                    <span style="font-weight: bold; font-size: larger;">{{ metric.Category }} - {{ metric.Title }}</span><br />
                    <span style="font-size: larger;">{{ getMetricHelp(metric) }}</span><br />
                    <span style="font-style: italic;">{{ Translate('Source(s):') }} {{ metric.Sources.join(', ') }}</span>
                    <br v-if="metric.Dependencies && metric.Dependencies.Modules.length" />
                    <span style="font-style: italic;" v-if="metric.Dependencies && metric.Dependencies.Modules.length">{{ Translate('Module(s):') }} {{ metric.Dependencies.Modules.join(', ') }}</span>
                </q-tooltip>
            </q-btn>
        </div>
    </div>
</template>

<script>
    import common from './common.js';
    import methods from '@/Shared/methods';
    import { QBtn, QIcon, QMenu, QList, QItem, QItemSection, QTooltip, QSpace } from 'quasar';

    export default {
        components: { QBtn, QIcon, QMenu, QList, QItem, QItemSection, QTooltip, QSpace },
        data: function () {
            return {
                metricMenu: [],
            }
        },
        props: {
            items: null,
            columnLookup: null,
            isMetricInUse: null,
            getMetricItemIcon: null,
            showHelp: null,
            isDisabledMetric: null,
            isDisabledSource: null,
            addMetric: null,
            removeMetric: null,
            getMetricHelp: null,
            metric_types: null,
        },
        created() {
        },
        methods: {
            ...methods,
            getFunctionDisplayName(metric, func) {
                const col = this.columnLookup[metric.ColId];
                if (col)
                    return common.formatMetric({ type: col.SourceType, aggFunc: func });
                else
                    return func;
            },
            getMetricDisplayName(metric, func) {
                const col = this.columnLookup[metric.ColId];
                if (col)
                    return `${metric.Title} (${common.formatMetric({ headerName: col.Title, type: col.SourceType, aggFunc: func })})`;
                else
                    return metric.Title;
            },
            getMetricIconColor(item) {
                switch (this.getMetricItemIcon(item)) {
                    case 'mdi-checkbox-marked': return 'primary';
                    case 'mdi-checkbox-intermediate': return 'primary';
                    case 'mdi-checkbox-blank-outline':
                    default:
                        return undefined;
                }
            },
            getSortedFunctions(metric) {
                return metric.MetricAggs.FunctionList.toSorted((a, b) => common.byField(this.getFunctionDisplayName(metric, a), this.getFunctionDisplayName(metric, b)));
            },
        }
    }
</script>

<style scoped>
    .hidden-element {
        visibility: hidden;
    }

    .columns-two {
        column-count: 2;
    }

    .columns-three {
        column-count: 3;
    }

    .verydense {
        max-height: 24px;
        min-height: 24px;
    }

    .mediumdense {
        max-height: 28px;
        min-height: 28px;
    }

    p {
        font-size: 2em;
        text-align: center;
    }

    .metric_normal {
    }

    .metric_inuse {
        color: cornflowerblue;
    }

    .metric_usedup {
        color: silver;
    }
</style>
